import { Image, StyleSheet, Text, View } from '@react-pdf/renderer';
import { Style } from '@react-pdf/types';
import ColorChart from 'components/Export/Components/ColorCharts';
import { ImageExtension, ListVariation, ProductImage, ShopCompleteVariation, TagPosition } from 'microshop-api';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeWebColors } from 'styled-components';
import defaultTheme from 'theme/defaultTheme';
import { KeyValuePair } from 'types/KeyValuePair';
import getImage, { ImageType } from 'utils/getImage';
import { getProductDescriptionString } from 'utils/getProductDescription';
import ExportPdfContext from '../ExportPdfContext';
import { PdfProduct } from '../exportPdfTypes';

const styles = StyleSheet.create({
    main: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        marginBottom: 16,
    },
    p: {
        marginBottom: 2,
    },
    details: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    detailsHeader: {
        justifyContent: 'space-between',
        borderBottom: '#9C9C9C',
        borderBottomWidth: 1,
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        marginBottom: 8,
        paddingBottom: 4,
    },
    detailsHeaderProductName: {
        fontFamily: 'PDF Font Bold',
        fontSize: 16,
        lineHeight: 1.3,
        width: 240,
    },
    detailsHeaderProductNumber: {
        fontSize: 8,
        flexGrow: 1,
        fontColor: '#757575',
        marginBottom: 10,
    },
    detailsHeaderProductBrand: {
        fontSize: 10,
        fontFamily: 'PDF Font Bold',
        textTransform: 'uppercase',
    },
    detailsHeaderLogo: {
        width: 60,
        paddingBottom: 4,
        display: 'flex',
        alignSelf: 'flex-end',
    },

    detailsHeaderPrices: {
        marginTop: 'auto',
    },
    detailsHeaderPrice: {
        display: 'flex',
        flexDirection: 'row',
        fontSize: 8,
        alignSelf: 'flex-end',
    },
    detailsHeaderPriceHeading: {
        fontSize: 8,
        fontFamily: 'PDF Font Bold',
        lineHeight: 0,
        display: 'flex',
        alignSelf: 'flex-end',
        paddingRight: 2,
        fontWeight: 1000,
    },
    detailsHeaderPricePricing: {
        lineHeight: 1.3,
        fontSize: 8,
        display: 'flex',
        alignSelf: 'flex-end',
    },
    detailsBody: {
        display: 'flex',
        flexDirection: 'row',
    },
    detailsBodyAttributes: {
        flex: 1,
    },
    variations: {
        paddingTop: 4,
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        display: 'flex',
        flexDirection: 'row',
    },
    variationColor: {
        flexDirection: 'column',
        alignItems: 'center',
        margin: '0px 4px',
        marginBottom: 8,
    },
    variationColorDot: {
        borderRadius: 10,
        height: 20,
        width: 20,
        border: '1px solid gray',
    },

    additionalInformation: {
        display: 'flex',
        flexDirection: 'column',
    },
    careInstruction: {
        width: 30,
        padding: 4,
    },
    careInstructionList: {
        display: 'flex',
        flexDirection: 'column',
    },
    flexRow: {
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
    },
    certification: {
        maxWidth: 60,
        alignItems: 'center',
    },
    cerificationImage: {
        maxWidth: 60,
        maxHeight: 30,
        paddingBottom: 4,
        objectFit: 'contain',
    },
    flexRowWrap: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
    attributeHeader: {
        fontSize: 8,
        marginBottom: 2,
        fontFamily: 'PDF Font',
        paddingRight: 4,
    },
    attributeValue: {
        fontSize: 8,
        marginBottom: 2,
        fontFamily: 'PDF Font',
    },
    imageSection: {
        maxWidth: 200,
        maxHeight: 300,
        display: 'flex',
        flexDirection: 'column',
    },
    imageSectionEnvironmental: {
        maxWidth: 216,
        maxHeight: 316,
        marginLeft: -16,
        marginTop: -16,
        display: 'flex',
        flexDirection: 'column',
    },
    imageSectionSmall: {
        paddingLeft: 8,
        maxWidth: 180,
        maxHeight: 180,
        paddingRight: 8,
    },
    imagePrimary: {
        maxWidth: 200,
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        paddingRight: 16,
    },
    imagePrimaryEnvironmental: {
        maxWidth: 216,
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        paddingRight: 16,
    },
    imagePrimaryRight: {
        display: 'flex',
        alignSelf: 'flex-end',
    },
    secondaryImages: {
        flexWrap: 'wrap',
        maxWidth: 200,
        display: 'flex',
        flexDirection: 'row',
    },
    secondaryImagesRight: {
        maxWidth: 180,
        display: 'flex',
        flexDirection: 'row',
        alignSelf: 'flex-end',
    },
    regularText: {
        fontFamily: 'PDF Font',
        fontSize: 8,
    },
    extraInfoTextContainer: {
        padding: 8,
        marginTop: 8,
    },
    extraInfoText: {
        fontFamily: 'Times-Italic',
        fontStyle: 'italic',
        fontSize: 7,
        marginTop: 2,
    },
    pictureImageSmall: {
        maxHeight: 100,
        paddingTop: 2,
        paddingBottom: 2,
        paddingHorizontal: 1,
    },
    pictureImage: {
        maxHeight: 50,
        maxWidth: 50,
        paddingTop: 2,
        paddingBottom: 2,
        paddingHorizontal: 1,
    },
    sizeGuide: {
        maxWidth: '90%',
    },
    sectionHeader: {
        fontSize: 10,
        fontFamily: 'PDF Font Bold',
        marginBottom: 2,
        textTransform: 'capitalize',
    },
    sectionContainer: {
        marginBottom: 16,
    },
    barcodeContainer: {
        maxHeight: 95,
        maxWidth: 150,
    },
});

type ProductProps = {
    product: PdfProduct;
    newPage: boolean;
    showVariationImages: boolean;
    showImagePicture?: boolean;
};

function getFrontImage(product: PdfProduct, variation?: ShopCompleteVariation): ProductImage | undefined {
    if (variation) {
        if (variation.image?.customUrl) return variation.image;
        return variation.images?.[0];
    }

    if (product.image?.customUrl) return product.image;
    return product.pictures?.[0];
}

export const PdfProductPageFull = ({ product, newPage, showVariationImages, showImagePicture }: ProductProps) => {
    const { t } = useTranslation();
    const { orientation } = useContext(ExportPdfContext);
    const hasRelevantVariations = !!product?.variations?.length;
    const hasAdditionalInformation =
        (product.marks?.certifications?.length ?? 0) > 0 || (product.marks?.careInstructions?.length ?? 0) > 0;

    // Images
    const selectedVariation = product.variations?.length
        ? product.variations.find((v) => v.featured) ?? product.variations[0]
        : undefined;
    const imagePicture = product.pictures?.find((image) => image.type === 'Imagepicture');
    const imagePictureUrl = imagePicture ? getImage(imagePicture, ImageType.Preview) : undefined;
    const frontImage = getFrontImage(product, selectedVariation);
    const imageUrl = getImage(frontImage, ImageType.Preview, ImageExtension.Jpg);
    const anglePictures = selectedVariation?.images ?? product.pictures;

    // Variations
    const colorVariations = product.variations?.map((variation) => {
        const colors = getColorsFromVariation(variation);
        return {
            hexColors: colors?.length ? colors : null,
            colorCode: variation.colorCode ?? null,
            colorName: variation.color ?? null,
            variationImage: getImage(variation.image, ImageType.ThumbNail, ImageExtension.Jpg),
        };
    });

    const isProductInfoAvailable = product.customTags?.some((tag) => tag.position === TagPosition.ProductInfo);

    const description = getProductDescriptionString(product.description, product.customDescription);
    const paragraphs = splitNewLines(description);

    return (
        <View break={newPage}>
            <Section style={styles.main}>
                {/* Left side */}
                <ImageSection
                    layout={showImagePicture ? 'environment' : 'left'}
                    imageUrl={showImagePicture ? imagePictureUrl : imageUrl}
                    productImages={showImagePicture ? null : anglePictures}
                />

                {/* Right side */}
                <View style={styles.details}>
                    <Section style={styles.detailsHeader}>
                        <View>
                            <Text style={styles.detailsHeaderProductBrand}>{product.productBrandName}</Text>
                            <Text style={styles.detailsHeaderProductNumber}>{product.productNumber}</Text>
                            <Text style={styles.detailsHeaderProductName}>{product.productName}</Text>
                        </View>
                        <View>
                            {product.marks?.brand[0]?.image?.fileName && (
                                <Image
                                    cache={true}
                                    style={styles.detailsHeaderLogo}
                                    src={getImage(
                                        product.marks?.brand[0]?.image,
                                        ImageType.ThumbNail,
                                        ImageExtension.Png,
                                    )}
                                />
                            )}
                            <View style={styles.detailsHeaderPrices}>
                                {product.price?.customer && (
                                    <Text
                                        style={
                                            // ? styles.detailsHeaderPriceHeading
                                            styles.detailsHeaderPricePricing
                                        }
                                    >
                                        {product.price.customer.str}
                                    </Text>
                                )}
                            </View>
                        </View>
                    </Section>
                    <Section style={styles.detailsBody}>
                        <View style={styles.detailsBodyAttributes}>
                            <Section>
                                {paragraphs?.map((p, i) => (
                                    <Text key={i} style={[styles.regularText, styles.p]}>
                                        {p}
                                    </Text>
                                ))}
                            </Section>

                            {product.attributes && Object.keys(product.attributes)?.length ? (
                                <Section>
                                    <Text style={styles.sectionHeader}>{t('product_information')}</Text>
                                    <Attributes attributes={product.attributes} />
                                    {isProductInfoAvailable && (
                                        <ExtraInformation
                                            text={product.customTags?.[0].value}
                                            colorString={'#FBF8E3'}
                                        />
                                    )}
                                </Section>
                            ) : null}

                            {product.sizeSummary ? (
                                <Section>
                                    <Text style={styles.sectionHeader}>{t('size', 'Size')}</Text>
                                    <Text style={styles.regularText}>{product.sizeSummary}</Text>
                                </Section>
                            ) : null}
                        </View>

                        {showImagePicture && (
                            <ImageSection layout="right" imageUrl={imageUrl} productImages={anglePictures} />
                        )}
                    </Section>

                    {/* Variations */}
                    {hasRelevantVariations && (
                        <Section>
                            <Text style={styles.sectionHeader}>{t('colors')}</Text>
                            <ColorChart colors={colorVariations} showVariationImages={showVariationImages} />
                        </Section>
                    )}
                    {hasAdditionalInformation && (
                        <View style={orientation === 'landscape' ? styles.flexRowWrap : undefined}>
                            {(product.marks?.careInstructions?.length ?? 0) > 0 && (
                                <Section>
                                    <Text style={styles.sectionHeader}>
                                        {t('care_instructions', 'Care instructions')}
                                    </Text>
                                    <View style={styles.flexRow}>
                                        {product.marks?.careInstructions
                                            ?.filter((care) => care.image?.fileName)
                                            .map(
                                                (instruction, index) =>
                                                    instruction.image?.fileName && (
                                                        <Image
                                                            cache={true}
                                                            key={index}
                                                            style={styles.careInstruction}
                                                            src={getImage(
                                                                instruction.image,
                                                                ImageType.ThumbNail,
                                                                ImageExtension.Png,
                                                            )}
                                                        />
                                                    ),
                                            )}
                                    </View>
                                    <View style={styles.careInstructionList}>
                                        {product.marks?.careInstructions
                                            ?.filter((care) => care.image?.fileName === null)
                                            .map((instruction, index) => (
                                                <Text key={index} style={styles.regularText}>
                                                    {'\u2022 ' + instruction.text}
                                                </Text>
                                            ))}
                                    </View>
                                </Section>
                            )}
                            {(product.marks?.certifications?.length ?? 0) > 0 && (
                                <Section>
                                    <Text style={styles.sectionHeader}>{t('certificates', 'Certifications')}</Text>
                                    <View style={styles.flexRow}>
                                        {product.marks?.certifications
                                            ?.filter((cert) => cert.image)
                                            .map(
                                                (certification, index) =>
                                                    certification.image?.fileName && (
                                                        <View key={index} style={styles.certification}>
                                                            <Image
                                                                style={styles.cerificationImage}
                                                                cache={true}
                                                                src={getImage(
                                                                    certification.image,
                                                                    ImageType.ThumbNail,
                                                                    ImageExtension.Png,
                                                                )}
                                                            />
                                                            <Text style={styles.regularText}>{certification.text}</Text>
                                                        </View>
                                                    ),
                                            )}
                                    </View>
                                </Section>
                            )}
                        </View>
                    )}
                </View>
            </Section>
        </View>
    );
};
export default PdfProductPageFull;

type ImageSectionProps = {
    layout: 'left' | 'right' | 'environment';
    imageUrl?: string | null;
    productImages?: ProductImage[] | null;
};

const ImageSection = ({ layout: style, imageUrl, productImages }: ImageSectionProps) => {
    const angleImages = productImages
        ?.slice(1)
        ?.filter((p) => p.type === 'Productpicture')
        .slice(0, 8);

    function getSectionStyle() {
        switch (style) {
            case 'left':
                return styles.imageSection;
            case 'right':
                return styles.imageSectionSmall;
            case 'environment':
                return styles.imageSectionEnvironmental;
        }
    }

    function getImageStyle() {
        switch (style) {
            case 'left':
                return styles.imagePrimary;
            case 'right':
                return styles.imagePrimaryRight;
            case 'environment':
                return styles.imagePrimaryEnvironmental;
        }
    }
    const sectionStyle = getSectionStyle();
    const imageStyle = getImageStyle();

    const renderImages = (
        <View style={sectionStyle}>
            <View style={imageStyle}>{imageUrl && <Image cache={true} src={imageUrl} />}</View>
            {angleImages?.length ? (
                <View style={style === 'right' ? [styles.secondaryImagesRight] : [styles.secondaryImages]}>
                    {angleImages?.slice(0, 4).map((p, index) => (
                        <Image
                            cache={true}
                            key={index}
                            style={style === 'right' ? styles.pictureImageSmall : styles.pictureImage}
                            src={getImage(p ?? undefined, ImageType.ThumbNail, ImageExtension.Jpg)}
                        />
                    ))}
                </View>
            ) : null}
        </View>
    );

    return style === 'right' ? renderImages : <Section>{renderImages}</Section>;
};

const Attributes = ({ attributes }: { attributes: KeyValuePair<string[]> }) => {
    const { t } = useTranslation();
    if (!attributes) return null;

    const fabricParagraphs = attributes.fabrics ? splitNewLines(attributes.fabrics.join(', ')) : [];

    const {
        gender,
        weight,
        height,
        measure,
        presentationDate,
        colorComment,
        printCode,
        designer,
        packaging,
        techniqueComment,
    } = attributes;
    return (
        <>
            {fabricParagraphs.map((p) => (
                <Attribute>{p}</Attribute>
            ))}
            {gender && <Attribute label={t('gender', 'Gender')}>{gender.join(', ')}</Attribute>}
            {weight && <Attribute label={t('weight', 'Weight')}>{weight.join(', ')}</Attribute>}
            {height && <Attribute label={t('height', 'Height')}>{height.join(', ')}</Attribute>}
            {measure && <Attribute label={t('measure')}>{measure.join(', ')}</Attribute>}
            {presentationDate && <Attribute label={t('presentationDate')}>{presentationDate.join(', ')}</Attribute>}
            {colorComment && <Attribute label={t('colorComment')}>{colorComment.join(', ')}</Attribute>}
            {printCode && <Attribute label={t('printCode')}>{printCode.join(', ')}</Attribute>}
            {designer && <Attribute label={t('designer')}>{designer.join(', ')}</Attribute>}
            {packaging && <Attribute label={t('packaging')}>{packaging.join(', ')}</Attribute>}
            {techniqueComment && <Attribute label={t('techniqueComment')}>{techniqueComment.join(', ')}</Attribute>}
        </>
    );
};

const Attribute = ({ label, children }: { label?: string; children: React.ReactNode }) => (
    <View style={styles.flexRowWrap}>
        {label && <Text style={styles.attributeHeader}>{`${label}:`}</Text>}
        <Text style={styles.attributeValue}>{children}</Text>
    </View>
);

const ExtraInformation = ({ text, colorString }: { text?: string | null; colorString: string }) => {
    return (
        <View style={[styles.extraInfoTextContainer, { backgroundColor: colorString }]}>
            <Text style={styles.extraInfoText}>{text}</Text>
        </View>
    );
};

const Section = ({ style, children }: { style?: Style | Style[]; children: React.ReactNode }) => (
    <View style={style || styles.sectionContainer}>{children}</View>
);

// utils
const getColorsFromVariation = (variation: ListVariation) => {
    if (variation.shadeColors?.length) {
        return variation.shadeColors.map((sc) => sc.toLowerCase());
    } else if (variation.webColors?.length) {
        return variation.webColors.map((wc) => defaultTheme.webColors[wc.toLowerCase() as keyof ThemeWebColors]);
    } else {
        return undefined;
    }
};

// Bug in this version of react-pdf makes new lines crash on null reference.
// We split them and render each as separate element for now.
function splitNewLines(text: string) {
    return text
        ?.replace('\r', '')
        .split('\n')
        .map((s) => s.trim());
}
